import { useEffect, useState, useRef } from "react";
import { X, Loader2, FileText, Printer, Download, CheckCircle2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { formatRupiah, formatDate } from "@/components/booking/bookingMeta";

const STATUS = {
  draft: { label: "Draft", cls: "bg-muted text-muted-foreground border-border" },
  pending_acceptance: { label: "Menunggu Persetujuan", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  accepted: { label: "Disetujui", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  cancelled: { label: "Dibatalkan", cls: "bg-rose-100 text-rose-700 border-rose-200" },
};

export default function RentalAgreementModal({ open, bookingId, role = "customer", onClose, onAccepted }) {
  const [agr, setAgr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const docRef = useRef(null);

  useEffect(() => {
    if (!open || !bookingId) return;
    setAgr(null); setErr(""); setAgree(false);
    setLoading(true);
    apiClient.get(`/bookings/${bookingId}/agreement`)
      .then((r) => setAgr(r.data))
      .catch((e) => setErr(formatApiErrorDetail(e?.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [open, bookingId]);

  if (!open) return null;

  const accept = async () => {
    setSubmitting(true);
    try {
      const { data } = await apiClient.post(`/bookings/${bookingId}/agreement/accept`, { accepted: true });
      setAgr(data);
      toast.success("Perjanjian sewa disetujui.");
      onAccepted?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const printDoc = () => {
    const html = docRef.current?.innerHTML;
    if (!html) return;
    const w = window.open("", "_blank", "width=800,height=900");
    if (!w) { toast.error("Popup diblokir. Izinkan popup untuk mencetak."); return; }
    w.document.write(`<html><head><title>${agr?.agreement_number || "Rental Agreement"}</title>
      <style>body{font-family:Georgia,serif;padding:32px;color:#1a1a1a;line-height:1.6}
      h1{font-size:20px}h2{font-size:14px;margin-top:18px}table{width:100%;border-collapse:collapse}
      td{padding:4px 0}.muted{color:#666;font-size:12px}</style></head><body>${html}</body></html>`);
    w.document.close(); w.focus(); w.print();
  };

  const download = () => {
    const html = docRef.current?.innerHTML || "";
    const blob = new Blob([`<html><head><meta charset="utf-8"><title>${agr?.agreement_number}</title></head><body>${html}</body></html>`], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${agr?.agreement_number || "rental-agreement"}.html`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const st = STATUS[agr?.status] || STATUS.draft;
  const canAccept = role === "customer" && agr?.status === "pending_acceptance";

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 backdrop-blur-sm p-4" data-testid="rental-agreement-modal">
      <div className="w-full max-w-2xl bg-card border border-border rounded-2xl shadow-xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
            <FileText size={18} className="text-primary" /> Rental Agreement
          </h3>
          <div className="flex items-center gap-2">
            {agr && (
              <>
                <button onClick={printDoc} data-testid="agreement-print-button" title="Print"
                  className="grid place-items-center w-9 h-9 rounded-full border border-border hover:bg-secondary/60"><Printer size={16} /></button>
                <button onClick={download} data-testid="agreement-download-button" title="Download"
                  className="grid place-items-center w-9 h-9 rounded-full border border-border hover:bg-secondary/60"><Download size={16} /></button>
              </>
            )}
            <button onClick={onClose} data-testid="agreement-close-button" className="text-muted-foreground hover:text-foreground"><X size={20} /></button>
          </div>
        </div>

        {loading ? (
          <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-primary" size={26} /></div>
        ) : err ? (
          <div className="p-8 text-center" data-testid="agreement-error">
            <p className="text-sm text-muted-foreground">{err}</p>
            <button onClick={onClose} className="mt-4 px-5 py-2.5 rounded-full border border-border text-sm font-semibold hover:bg-secondary/60">Kembali</button>
          </div>
        ) : agr ? (
          <>
            <div className="overflow-y-auto px-6 py-5" ref={docRef} data-testid="agreement-document">
              <div className="text-center border-b border-border pb-4">
                <h1 className="font-heading text-xl font-bold tracking-tight">RENTAL AGREEMENT / PERJANJIAN SEWA</h1>
                <p className="mt-1 text-sm text-muted-foreground">No: <span className="font-mono" data-testid="agreement-number">{agr.agreement_number}</span> · Versi {agr.version}</p>
                <span className={`inline-block mt-2 text-xs font-semibold px-2.5 py-0.5 rounded-full border ${st.cls}`} data-testid="agreement-status" data-agreement-status={agr.status}>
                  {st.label}
                </span>
              </div>

              <div className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
                <Info label="Property" value={agr.property_name} />
                <Info label="Kamar" value={`${agr.room_number || "-"}${agr.room_name ? " · " + agr.room_name : ""}`} />
                <Info label="Penyewa (Customer)" value={agr.customer_name} />
                <Info label="Pemilik (Owner)" value={agr.owner_name} />
                <Info label="Tanggal Mulai" value={formatDate(agr.start_date)} />
                <Info label="Tanggal Selesai" value={formatDate(agr.end_date)} />
              </div>

              <h2 className="font-heading text-base font-semibold mt-5 mb-2">Rincian Keuangan</h2>
              <div className="rounded-xl border border-border bg-secondary/20 p-4 text-sm space-y-1.5">
                <Row label="Harga Kamar (total masa sewa)" value={formatRupiah(agr.room_price_total)} testid="agreement-room-price" />
                <Row label="Deposit" value={formatRupiah(agr.deposit_amount)} testid="agreement-deposit" />
                <Row label="Total Kewajiban" value={formatRupiah(agr.total_obligation)} strong />
                <div className="border-t border-border my-1" />
                <Row label="Harga Kamar Dibayar" value={formatRupiah(agr.room_paid)} />
                <Row label="Sisa Harga Kamar" value={formatRupiah(agr.room_remaining)} muted />
                <Row label="Deposit Dibayar" value={formatRupiah(agr.deposit_paid)} />
                <Row label="Sisa Deposit" value={formatRupiah(agr.deposit_remaining)} muted />
              </div>

              <h2 className="font-heading text-base font-semibold mt-5 mb-2">Ketentuan Sewa (Terms &amp; Conditions)</h2>
              <ol className="space-y-2 text-sm">
                {(agr.terms_snapshot?.sections || []).map((s) => (
                  <li key={s.no} data-testid={`agreement-term-${s.no}`}>
                    <span className="font-semibold">{s.no}. {s.title}.</span>{" "}
                    <span className="text-muted-foreground whitespace-pre-line">{s.body}</span>
                  </li>
                ))}
              </ol>

              {agr.customer_accepted_at && (
                <div className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm" data-testid="agreement-acceptance-info">
                  <p className="font-semibold text-emerald-700 flex items-center gap-1.5"><CheckCircle2 size={15} /> Disetujui oleh Customer</p>
                  <p className="mt-1 text-muted-foreground">Waktu: {formatDate(agr.customer_accepted_at)} ({new Date(agr.customer_accepted_at).toLocaleTimeString("id-ID")})</p>
                </div>
              )}

              <p className="mt-5 text-xs text-muted-foreground flex items-start gap-1.5">
                <ShieldAlert size={14} className="mt-0.5 shrink-0" /> {agr.disclaimer}
              </p>
            </div>

            {canAccept && (
              <div className="border-t border-border px-6 py-4 space-y-3">
                <label className="flex items-start gap-2.5 text-sm cursor-pointer">
                  <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)}
                    data-testid="agreement-accept-checkbox" className="mt-0.5 w-4 h-4 accent-primary" />
                  <span>Saya telah membaca dan menyetujui Rental Agreement / Perjanjian Sewa ini.</span>
                </label>
                <button onClick={accept} disabled={!agree || submitting}
                  data-testid="agreement-accept-button"
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-accent text-accent-foreground font-semibold hover:scale-[1.01] active:scale-95 transition-transform disabled:opacity-50 disabled:hover:scale-100">
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Setujui Perjanjian
                </button>
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
      <p className="font-medium text-foreground">{value || "-"}</p>
    </div>
  );
}
function Row({ label, value, strong, muted, testid }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className={muted ? "text-muted-foreground text-xs" : "text-muted-foreground"}>{label}</span>
      <span className={strong ? "font-bold text-foreground" : "font-medium text-foreground"} data-testid={testid}>{value}</span>
    </div>
  );
}
